import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../../redux/cartSlice";
import { useGetProductsByID, useGetProductsByCat } from "../../backend/hooks";
import WhatsAppButton from '../../common/whatsapp';
import { toast, ToastContainer } from 'react-toastify';
import { normalizeRichText } from '../../utils/richText';
import { getVariants, getDefaultVariant, getUnitPrice, getShipping, hasVariants, getPriceRange } from '../../utils/variants';
import { useCurrency, formatMoney, formatPrice } from '../../utils/currency';
import WhatsAppEnquiryButton from '../../common/whatsappEnquiryButton';
import { Skeleton } from '../../common/skeleton';

export default function ProductDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currency, isPakistan } = useCurrency();
  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantName, setSelectedVariantName] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sliderRef = useRef(null);

  // Reset main image and quantity when product ID changes
  useEffect(() => {
    setMainImage(null);
    setQuantity(1);
    setSelectedVariantName(null);
  }, [id]);

  // 1. Fetch main product
  const { data, isLoading, error } = useGetProductsByID(id);

  // 2. Only fetch related products after we have a category_id
  const categoryId = data?.category_id;
  const { data: relatedData, isLoading: relatedLoading } = useGetProductsByCat(categoryId, {
    enabled: !!categoryId,
  });

  const getTagsArray = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) {
      return tags.flatMap((item) => item.split(",").map((s) => s.trim()));
    }
    return tags.split(",").map((s) => s.trim());
  };

  // Set main image once product data loads (and image not already set)
  if (data && !mainImage && data.images?.length) {
    setMainImage(data.images[0]);
  }

  if (isLoading) {
    return (
      <main className="pt-32 pb-20 max-w-7xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-3">
          <Skeleton className="h-4 w-12 rounded" />
          <Skeleton className="h-4 w-24 rounded" />
          <Skeleton className="h-4 w-40 rounded" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Images */}
          <div className="lg:col-span-7 space-y-4">
            <Skeleton className="h-[400px] md:h-[500px] w-full rounded-xl" />
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} className="aspect-square rounded-lg" />
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-5 space-y-6">
            <div className="flex gap-2">
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <div className="space-y-3">
              <Skeleton className="h-9 w-full rounded-lg" />
              <Skeleton className="h-9 w-2/3 rounded-lg" />
            </div>
            <Skeleton className="h-8 w-1/3 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-11/12 rounded" />
              <Skeleton className="h-4 w-3/4 rounded" />
            </div>
            <div className="flex gap-4">
              <Skeleton className="h-14 w-32 rounded-xl" />
              <Skeleton className="h-14 flex-1 rounded-xl" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-32 pb-20 max-w-7xl mx-auto px-6 text-center">
        <div className="text-lg text-error">Error: {error.message}</div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-32 pb-20 max-w-7xl mx-auto px-6 text-center">
        <div className="text-lg text-on-surface-variant">Product not found.</div>
      </main>
    );
  }

  const product = data;
  const images = product.images || [];
  const tagsArray = getTagsArray(product.tags);
  // Named options ('Standard Quality', 'Large', ...) each carry their own
  // price. Track the selection by name so a refetch cannot leave a stale object.
  const variants = getVariants(product);
  const productHasVariants = variants.length > 0;
  const selectedVariant = productHasVariants
    ? variants.find((variant) => variant.name === selectedVariantName) || getDefaultVariant(product)
    : null;
  const unitPrice = getUnitPrice(product, selectedVariant, currency);
  const shipping = getShipping(product, currency);

  // Marking the whole product out of stock overrides every option
  const productInStock = product.in_stock === "yes";
  const inStock = productHasVariants
    ? productInStock && Boolean(selectedVariant && selectedVariant.in_stock === "yes")
    : productInStock;
  const categoryName = product.category_name || "Products";

  // Related products – filter out current product
  const relatedProducts = relatedData?.filter((p) => p.id !== product.id) || [];

  const increaseQuantity = () => setQuantity((q) => q + 1);
  const decreaseQuantity = () => setQuantity((q) => (q > 1 ? q - 1 : 1));

  const handleAddToCart = () => {
    if (productHasVariants && !selectedVariant) {
      toast.error("Please choose an option first");
      return;
    }
    dispatch(addToCart({ product, quantity, variant: selectedVariant }));
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Cache‑busting: append timestamp to image URL
  const getImageUrl = (path) => {
    if (!path) return "https://via.placeholder.com/800x450";
    const base = process.env.REACT_APP_BE_URL + path;
    return `${base}?t=${Date.now()}`;
  };

  // Slider navigation
  const scrollSlider = (direction) => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const cardWidth = container.firstChild?.offsetWidth || 0;
    const gap = 16; // gap-4 = 16px
    const scrollAmount = (cardWidth + gap) * (window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 3 : 2);
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <main className="pt-32 pb-20 max-w-7xl mx-auto px-4 md:px-6">
      <WhatsAppButton />
      <ToastContainer />

      {/* Breadcrumb */}
      <nav className="mb-8 flex items-center gap-2 text-sm font-medium text-on-surface-variant">
        <span>Shop</span>
        <span className="material-symbols-outlined text-sm">chevron_right</span>
        <span>{categoryName}</span>
        <span className="material-symbols-outlined text-sm">chevron_right</span>
        <span className="text-primary font-bold">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Left column – images */}
        <div className="lg:col-span-7 space-y-4">
          <div
            className="rounded-xl overflow-hidden bg-surface-container-low relative group cursor-pointer"
            onClick={openModal}
          >
            <div className="h-[400px] md:h-[500px] w-full">
              <img
                alt={product.title}
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                src={getImageUrl(mainImage)}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
              <span className="bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                Click to enlarge
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 md:gap-4">
            {images.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setMainImage(img)}
                className={`rounded-lg overflow-hidden border-2 bg-surface-container-low aspect-square cursor-pointer transition-all ${
                  mainImage === img
                    ? "border-primary"
                    : "border-transparent hover:border-outline-variant"
                }`}
              >
                <img
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                  src={getImageUrl(img)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-5 space-y-6">
          <header className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {tagsArray.map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-secondary-container text-on-secondary-container px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                >
                  {tag}
                </span>
              ))}
              <span
                className={`${
                  inStock ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                } px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest`}
              >
                {inStock ? "In Stock" : "Out of Stock"}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-on-surface leading-tight tracking-tight">
              {product.title}
            </h1>
            <div className="flex items-baseline gap-4">
              <span className="text-3xl font-bold text-primary">
                {formatPrice(unitPrice)}
              </span>
              {selectedVariant && (
                <span className="text-sm font-semibold text-on-surface-variant">
                  {selectedVariant.name}
                </span>
              )}
            </div>
          </header>

          {shipping.available && shipping.amount > 0 && (
            <p className="flex items-center gap-2 text-sm text-on-surface-variant">
              <span className="material-symbols-outlined text-base">local_shipping</span>
              Delivery charge: {formatMoney(shipping.amount, shipping.currency)}
            </p>
          )}

          <div className="space-y-6 text-on-surface-variant leading-relaxed">
            <p className="text-lg break-words">{product.description}</p>
          </div>

          {productHasVariants && (
            <div className="space-y-3 pt-2">
              <div className="flex items-baseline justify-between">
                <h3 className="text-sm font-bold uppercase tracking-widest text-on-surface-variant font-label">
                  Choose an option
                </h3>
                <span className="text-xs text-on-surface-variant">
                  {variants.length} available
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {variants.map((variant) => {
                  const isSelected = Boolean(selectedVariant && selectedVariant.name === variant.name);
                  const isSoldOut = variant.in_stock !== "yes";
                  const optionPrice = getUnitPrice(product, variant, currency);
                  return (
                    <button
                      key={variant.name}
                      type="button"
                      onClick={() => setSelectedVariantName(variant.name)}
                      disabled={isSoldOut}
                      aria-pressed={isSelected}
                      className={`text-left rounded-xl border-2 px-4 py-3 transition-all ${
                        isSelected
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-outline-variant hover:border-primary/50"
                      } ${isSoldOut ? "opacity-50 cursor-not-allowed" : "active:scale-[0.98]"}`}
                    >
                      <span className="block text-sm font-bold text-on-surface break-words">
                        {variant.name}
                      </span>
                      <span className="block text-lg font-extrabold text-primary mt-0.5">
                        {formatPrice(optionPrice)}
                      </span>
                      {isSoldOut && (
                        <span className="block text-[10px] font-bold uppercase tracking-widest text-error mt-1">
                          Out of stock
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4 pt-2">
            <div className="flex gap-4 flex-wrap sm:flex-nowrap">
              <div className="flex items-center bg-surface-container-highest rounded-xl px-4 py-2">
                <button
                  onClick={decreaseQuantity}
                  className="p-1 hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined">remove</span>
                </button>
                <span className="px-6 font-bold text-on-surface">{quantity}</span>
                <button
                  onClick={increaseQuantity}
                  className="p-1 hover:text-primary transition-colors"
                >
                  <span className="material-symbols-outlined">add</span>
                </button>
              </div>
              {isPakistan ? (
                <button
                  onClick={handleAddToCart}
                  disabled={!inStock}
                  className={`flex-1 bg-gradient-to-br from-primary to-primary-container text-on-primary font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all scale-100 active:scale-95 flex justify-center items-center gap-2 ${
                    !inStock ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <span className="material-symbols-outlined">add_shopping_cart</span>
                  Add to Cart
                </button>
              ) : (
                <WhatsAppEnquiryButton
                  product={product}
                  variant={selectedVariant}
                  quantity={quantity}
                  currency={currency}
                  className="flex-1 py-4 rounded-xl shadow-lg hover:shadow-xl"
                  iconClassName="w-6 h-6"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Technical Description */}
      {product.technical_description && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-on-surface mb-6">Technical Description</h2>
          <div className="bg-surface-container-lowest p-4 md:p-8 rounded-xl w-full overflow-hidden">
            <div
              className="text-on-surface-variant break-words
                         [&_img]:max-w-full [&_img]:h-auto
                         [&_table]:w-full [&_td]:border [&_th]:border [&_td]:px-2 [&_th]:px-2 [&_td]:py-1 [&_th]:py-1
                         [&_table]:block [&_table]:overflow-x-auto
                         [&_pre]:overflow-x-auto [&_code]:break-words [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap
                         [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
                         [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: normalizeRichText(product.technical_description) }}
            />
          </div>
        </div>
      )}

      {/* Related Products */}
      {categoryId && (
        <div className="mt-20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-on-surface">You Might Also Like</h2>
            {relatedProducts.length > 4 && (
              <div className="flex gap-2">
                <button
                  onClick={() => scrollSlider("left")}
                  className="p-2 rounded-full bg-surface-container-high hover:bg-surface-container-highest transition-colors"
                  aria-label="Previous"
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <button
                  onClick={() => scrollSlider("right")}
                  className="p-2 rounded-full bg-surface-container-high hover:bg-surface-container-highest transition-colors"
                  aria-label="Next"
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </div>

          {relatedLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-square rounded-xl mb-3" />
                  <Skeleton className="h-4 rounded w-3/4 mb-2" />
                  <Skeleton className="h-6 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : relatedProducts.length > 0 ? (
            <div
              ref={sliderRef}
              className="flex overflow-x-auto gap-4 pb-4 scroll-smooth snap-x snap-mandatory"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {relatedProducts.map((related) => (
                <Link
                  key={related.id}
                  to={`/ProductDetail/${related.id}`}
                  className="w-1/2 sm:w-1/3 lg:w-1/4 flex-shrink-0 snap-start bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="aspect-square overflow-hidden bg-surface-container-high">
                    <img
                      src={getImageUrl(related.images?.[0])}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-on-surface line-clamp-1">{related.title}</h4>
                    <p className="text-primary font-bold mt-1">
                      {getPriceRange(related, currency).from && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mr-1">
                          From
                        </span>
                      )}
                      {formatPrice(getPriceRange(related, currency))}
                    </p>
                    {isPakistan ? (
                      <button
                        className="w-full mt-3 py-2 bg-primary text-on-primary rounded-lg font-semibold text-sm hover:bg-primary-container transition-colors"
                        onClick={(e) => {
                          // A product with options has no single price, so let the
                          // click through to its own page where one can be picked.
                          if (hasVariants(related)) return;
                          e.preventDefault();
                          e.stopPropagation();
                          dispatch(addToCart(related));
                        }}
                      >
                        {hasVariants(related) ? "Choose an Option" : "Add to Cart"}
                      </button>
                    ) : (
                      <WhatsAppEnquiryButton
                        product={related}
                        currency={currency}
                        label="Enquire"
                        asButton
                        className="w-full mt-3 py-2 rounded-lg text-sm"
                        iconClassName="w-4 h-4"
                      />
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-on-surface-variant">No similar products found.</p>
          )}
        </div>
      )}

      {/* Image Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-8"
          onClick={closeModal}
        >
          <div
            className="relative max-w-5xl w-full h-full max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <span className="material-symbols-outlined text-3xl">close</span>
            </button>
            <img
              src={getImageUrl(mainImage)}
              alt={product.title}
              className="w-full h-full object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </main>
  );
}